import React, { useState } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import config from '../../../config/environment';

const heroImage = 'https://BelvoirCastle.b-cdn.net/Retail%20village/retail3.webp';

// Helper function to get the 3rd Sunday of a given month
const getThirdSunday = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const firstSunday = firstDay.getDay() === 0 ? 1 : (7 - firstDay.getDay() + 1);
  const thirdSunday = firstSunday + 14;
  return new Date(year, month, thirdSunday);
};

// Get all market dates and filter out past dates
const getUpcomingMarketDates = () => {
  const year = 2026;
  const months = [3, 4, 5, 6, 7, 8]; // April(3) to September(8) — 0-indexed months
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time for accurate date comparison
  
  const dates = months.map(month => {
    const eventDate = getThirdSunday(year, month);
    return {
      date: eventDate,
      formatted: eventDate.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    };
  });
  
  // Filter out past dates
  return dates.filter(item => {
    const eventDate = new Date(item.date);
    eventDate.setHours(0, 0, 0, 0);
    return eventDate >= today;
  });
};

const Page = styled.div`
  min-height: 100vh;
  padding-top: 120px;
  background-color: ${props => props.theme.colors.offWhite};
`;

const Hero = styled.div`
  width: 100%;
  height: 350px;
  background-image: ${props => `linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.7)), url(${props.image})`};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    height: 280px;
  }
`;

const HeroContent = styled.div`
  text-align: center;
  color: white;
  padding: 2rem;
  max-width: 800px;
`;

const Title = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 3rem;
  font-weight: 700;
  margin: 0 0 1rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.3rem;
  margin: 0;
  opacity: 0.95;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: -60px auto 3rem;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    margin-top: -40px;
    padding: 0 1.5rem;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0,0,0,0.12);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.5rem;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};
  text-align: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const DatesList = styled.div`
  background: ${props => props.theme.colors.offWhite};
  padding: 2rem;
  border-radius: 8px;
  margin: 2rem 0;
  
  h3 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.4rem;
    color: ${props => props.theme.colors.primary};
    margin: 0 0 1rem;
    text-align: center;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
  
  li {
    padding: 0.75rem;
    background: white;
    border-radius: 6px;
    text-align: center;
    font-weight: 500;
    color: ${props => props.theme.colors.primary};
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }
`;

const InfoHighlight = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin: 2rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const InfoBox = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}15 0%, ${props => props.theme.colors.accent}25 100%);
  border-radius: 8px;
  
  h4 {
    font-family: ${props => props.theme.fonts.main};
    font-size: 1.1rem;
    color: ${props => props.theme.colors.accent};
    margin: 0 0 0.5rem;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    font-size: 1rem;
    color: ${props => props.theme.colors.primary};
  }
`;

const LearnMoreLink = styled.button`
  background: transparent;
  color: ${props => props.theme.colors.accent};
  border: 2px solid ${props => props.theme.colors.accent};
  padding: 1rem 2.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: block;
  margin: 2rem auto 0;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

const FormSection = styled.div`
  margin: 2.5rem 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 500px;
  margin: 0 auto;
`;

const InputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Input = styled.input`
  padding: 1rem 1.2rem;
  border: 2px solid ${props => props.theme.colors.offWhite};
  border-radius: 8px;
  font-size: 1rem;
  font-family: ${props => props.theme.fonts.main};
  color: ${props => props.theme.colors.primary};
  transition: all 0.3s ease;
  background: white;
  
  &::placeholder {
    color: #999;
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.accent};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.accent}20;
  }
`;

const SubmitButton = styled.button`
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  padding: 1.2rem 2rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: ${props => props.theme.fonts.main};
  margin-top: 0.5rem;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusMessage = styled.div`
  text-align: center;
  padding: 1rem;
  border-radius: 8px;
  margin-top: 1rem;
  font-weight: 500;
  background: ${props => props.$error ? '#FEE2E2' : '#D1FAE5'};
  color: ${props => props.$error ? '#991B1B' : '#065F46'};
  border: 1px solid ${props => props.$error ? '#FCA5A5' : '#6EE7B7'};
`;

const AntiquesMarketRegister = () => {
  const navigate = useNavigate();
  const upcomingDates = getUpcomingMarketDates();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [status, setStatus] = useState({
    submitting: false,
    error: null,
    success: false,
    message: null
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, error: null, success: false, message: null });

    try {
      const response = await fetch(`${config.apiEndpoint}/newsletter/v1/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Origin': config.frontendUrl
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({
          ...formData,
          origin: window.location.origin,
          tags: ['Vintage and Craft Market']
        })
      });

      const data = await response.json();

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || 'Subscription failed');
      }

      setStatus({
        submitting: false,
        error: null,
        success: true,
        message: data?.status === 'already_subscribed' 
          ? 'You are already registered for market updates!'
          : data?.status === 'pending_confirmation'
            ? 'Thank you! Please check your email to confirm your registration.'
            : 'Thank you for registering! You will receive market updates.'
      });
      
      if (data?.status === 'new_subscription') {
        setFormData({ firstName: '', lastName: '', email: '' });
      }
    } catch (error) {
      setStatus({
        submitting: false,
        error: true,
        success: false,
        message: error.message || 'An error occurred. Please try again.'
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Register Interest - Vintage & Craft Market | Belvoir Castle</title>
        <meta name="description" content="Register your interest for the Vintage & Craft Market. Stay informed about upcoming dates, featured vendors, and exclusive market updates. 3rd Sunday every month, April–September." />
        <meta name="keywords" content="Belvoir Castle, antiques market registration, artisan market signup, craft market newsletter, retail village events" />
        <link rel="canonical" href="https://www.belvoircastle.com/events/antiques-artisan-market/register" />
        
        <meta property="og:title" content="Register Interest - Vintage & Craft Market" />
        <meta property="og:description" content="Register your interest for the monthly Vintage & Craft Market. Stay informed about upcoming dates and featured vendors." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/events/antiques-artisan-market/register" />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Register Interest - Vintage & Craft Market" />
        <meta name="twitter:description" content="Sign up for updates about the monthly Vintage & Craft Market at Belvoir Castle." />
      </Helmet>

      <Page>
        <Hero image={heroImage}>
          <HeroContent>
            <Title>Register Your Interest</Title>
            <Subtitle>Stay informed about the Vintage & Craft Market</Subtitle>
          </HeroContent>
        </Hero>

        <ContentWrapper>
          <Card>
            <SectionTitle>Sign Up for Market Updates</SectionTitle>
            <Description>
              Be the first to hear about upcoming market dates, featured vendors, special events, and exclusive news 
              about the Vintage & Craft Market.
            </Description>

            <InfoHighlight>
              <InfoBox>
                <h4>Monthly Event</h4>
                <p>3rd Sunday of each month</p>
              </InfoBox>
              <InfoBox>
                <h4>Free Entry</h4>
                <p>No ticket required</p>
              </InfoBox>
              <InfoBox>
                <h4>Time</h4>
                <p>10:00 - 16:00</p>
              </InfoBox>
            </InfoHighlight>

            <DatesList>
              <h3>2026 Market Dates</h3>
              {upcomingDates.length > 0 ? (
                <ul>
                  {upcomingDates.map((item, index) => (
                    <li key={index}>{item.formatted}</li>
                  ))}
                </ul>
              ) : (
                <p style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
                  All 2026 market dates have passed. Check back soon for 2027 dates!
                </p>
              )}
            </DatesList>

            <FormSection>
              <Form onSubmit={handleSubmit}>
                <InputGroup>
                  <Input 
                    type="text" 
                    name="firstName"
                    placeholder="First Name" 
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                  <Input 
                    type="text" 
                    name="lastName"
                    placeholder="Last Name" 
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </InputGroup>
                <Input 
                  type="email" 
                  name="email"
                  placeholder="Email Address" 
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <SubmitButton 
                  type="submit" 
                  disabled={status.submitting}
                >
                  {status.submitting ? 'REGISTERING...' : 'REGISTER YOUR INTEREST'}
                </SubmitButton>
                
                {(status.error || status.success) && (
                  <StatusMessage $error={status.error}>
                    {status.message}
                  </StatusMessage>
                )}
              </Form>
            </FormSection>

            <LearnMoreLink onClick={() => navigate('/events/antiques-artisan-market')}>
              Learn More About the Market
            </LearnMoreLink>
          </Card>
        </ContentWrapper>

        <Footer />
      </Page>
    </>
  );
};

export default AntiquesMarketRegister;
